import React, { useState, useEffect } from 'react';
import { ref, get, update, onValue } from 'firebase/database';
import { database } from '../firebase';

const AnniversaryAdminPanel = ({ anniversaryActive, onToggleAnniversary }) => {
    const [participants, setParticipants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [stock, setStock] = useState({});

    useEffect(() => {
        loadParticipants();

        // Listener para stock en tiempo real
        const stockRef = ref(database, 'anniversary_config/prizes_stock');
        const unsubscribeStock = onValue(stockRef, (snapshot) => {
            if (snapshot.exists()) {
                setStock(snapshot.val());
            }
        });

        return () => unsubscribeStock();
    }, []);

    const loadParticipants = async () => {
        setLoading(true);
        try {
            const participantsRef = ref(database, 'anniversary_participants');
            const snapshot = await get(participantsRef);
            if (snapshot.exists()) {
                const data = snapshot.val();
                const participantsArray = Object.entries(data).map(([id, values]) => ({
                    id,
                    ...values
                }));
                // Sort by referralCount descending
                setParticipants(participantsArray.sort((a, b) => (b.referralCount || 0) - (a.referralCount || 0)));
            }
        } catch (error) {
            console.error("Error loading participants:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleResetStock = async () => {
        if (!window.confirm("¿Seguro que quieres reiniciar el stock de premios?")) return;

        const initialStock = {
            "1 MES PREMIUM": 20,
            "BIMESTRE PREMIUM": 15,
            "SEMESTRAL PREMIUM": 10,
            "ANUAL PREMIUM": 5
        };

        try {
            await update(ref(database, 'anniversary_config/prizes_stock'), initialStock);
            alert("Stock inicializado correctamente (20, 15, 10, 5)");
        } catch (error) {
            console.error("Error resetting stock:", error);
            alert("Error al inicializar stock");
        }
    };

    const handleExportCSV = () => {
        if (participants.length === 0) return;

        const headers = ["Email", "Codigo Referido", "Referidos", "Premio", "Fecha Registro"];
        const csvRows = [
            headers.join(','),
            ...participants.map(p => {
                const date = p.registrationDate ? new Date(p.registrationDate).toLocaleString() : 'N/A';
                return [
                    `"${p.email || ''}"`,
                    `"${p.referralCode || ''}"`,
                    p.referralCount || 0,
                    `"${p.prizeWon || 'Pendiente'}"`,
                    `"${date}"`
                ].join(',');
            })
        ];

        const csvContent = csvRows.join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `participantes_aniversario_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const filteredParticipants = participants.filter(p =>
        (p.email?.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (p.referralCode?.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (p.estado?.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    if (loading) return <div className="loading">Cargando participantes...</div>;

    return (
        <div className="anniversary-admin-panel">
            <div className="panel-section">
                <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <div>
                        <h3>Gestión de Participantes - 1er Aniversario</h3>
                        <p className="section-desc">Monitoreo de referidos, exportación de datos y validación de adscripción.</p>
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button
                            onClick={handleResetStock}
                            className="btn-tactical"
                            style={{ padding: '10px 20px', background: '#ff9800', border: 'none', color: 'white', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold' }}
                        >
                            🔄 REINICIAR STOCK
                        </button>
                        <button
                            onClick={onToggleAnniversary}
                            className="btn-tactical"
                            style={{
                                padding: '10px 20px',
                                background: anniversaryActive ? '#f44336' : '#2196F3',
                                border: 'none',
                                color: 'white',
                                borderRadius: '5px',
                                cursor: 'pointer',
                                fontWeight: 'bold'
                            }}
                        >
                            {anniversaryActive ? '🚫 DESACTIVAR ANIVERSARIO' : '✅ ACTIVAR ANIVERSARIO'}
                        </button>
                        <button onClick={handleExportCSV} className="btn-tactical" style={{ padding: '10px 20px', background: '#4caf50', border: 'none', color: 'white', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold' }}>
                            📥 EXPORTAR CSV (EXCEL)
                        </button>
                    </div>
                </div>

                <div className="stock-management-section" style={{ marginBottom: '30px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '15px' }}>
                    {Object.entries(stock).length > 0 ? Object.entries(stock).map(([name, count]) => (
                        <div key={name} className="stock-card" style={{ background: 'rgba(255,255,255,0.05)', padding: '15px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.1)', textAlign: 'center' }}>
                            <div style={{ fontSize: '0.7rem', color: '#888', textTransform: 'uppercase', marginBottom: '5px' }}>{name}</div>
                            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: count > 0 ? 'var(--gold-matte)' : '#f44336' }}>{count}</div>
                            <div style={{ fontSize: '0.65rem', color: count > 0 ? '#4caf50' : '#f44336' }}>{count > 0 ? 'EN STOCK' : 'AGOTADO'}</div>
                        </div>
                    )) : (
                        <div style={{ padding: '20px', color: '#ff9800', background: 'rgba(255,152,0,0.1)', borderRadius: '10px', gridColumn: '1/-1' }}>
                            ⚠️ Stock no inicializado en RTDB. Haz clic en "REINICIAR STOCK" para comenzar.
                        </div>
                    )}
                </div>

                <div className="search-section" style={{ marginBottom: '20px' }}>
                    <input
                        type="text"
                        placeholder="Buscar por Email, Código o Estado..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="dashboard-search compact"
                        style={{ width: '100%', padding: '12px', background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', color: 'white', borderRadius: '8px' }}
                    />
                </div>

                <div className="licenses-table-container compact">
                    <table className="dashboard-table compact">
                        <thead>
                            <tr>
                                <th>Email</th>
                                <th>Código</th>
                                <th>Referidos</th>
                                <th>Premio</th>
                                <th>Fecha</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredParticipants.length > 0 ? filteredParticipants.map(p => (
                                <tr key={p.id}>
                                    <td className="cell-email" title={p.email}>{p.email}</td>
                                    <td style={{ fontWeight: 'bold', color: 'var(--accent)' }}>{p.referralCode}</td>
                                    <td style={{ textAlign: 'center', fontWeight: 'bold' }}>{p.referralCount || 0}</td>
                                    <td style={{ fontWeight: 'bold', color: p.prizeWon ? '#4caf50' : '#888' }}>
                                        {p.prizeWon || 'Pendiente'}
                                    </td>
                                    <td className="cell-date">{p.registrationDate ? new Date(p.registrationDate).toLocaleDateString() : 'N/A'}</td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan="5" className="no-data">No se encontraron participantes</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="license-stats">
                    <div className="stat-item">
                        <div className="stat-number">{participants.length}</div>
                        <div className="stat-label">Participantes Totales</div>
                    </div>
                    <div className="stat-item">
                        <div className="stat-number">{participants.reduce((acc, p) => acc + (p.referralCount || 0), 0)}</div>
                        <div className="stat-label">Descargas Generadas</div>
                    </div>
                    <div className="stat-item">
                        <div className="stat-number">{participants.filter(p => (p.referralCount || 0) > 0).length}</div>
                        <div className="stat-label">Con Referidos Activos</div>
                    </div>
                </div>
            </div>

            <style>{`
                .anniversary-admin-panel .dashboard-table.compact td { padding: 12px 8px; }
                .anniversary-admin-panel .cell-email { font-family: monospace; max-width: 150px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
                .anniversary-admin-panel .btn-tactical:hover { filter: brightness(1.1); transform: translateY(-1px); }
            `}</style>
        </div>
    );
};

export default AnniversaryAdminPanel;
